<template>
	<div>
		<v-card
			v-if="getEditCommentLength() == 0"
			class="comment-input-card"
			:class="{ 'mention-dropdown-open': isMentionDropdownOpen }"
			flat
			:elevation="0">
			<at-ta
				ref="mentionDropdown"
				v-click-outside="closeMention"
				name-key="name"
				:members="localUsers"
				:ats="['@']"
				suffix=" "
				hide-on-blur
				class="mention-dropdown"
				:class="{
					'mention-dropdown-mobile': display.smAndDown.value || lightBoxView,
					'mention-dropdown-open-bottom': openMentionDropdownBottom
				}"
				@at="adjustMentionDropdownDimensions">
				<div>
					<CharacterLimitedTextArea
						v-model="comment"
						v-test="'comments-input'"
						ref="commentField"
						no-resize
						:disabled="disableNewReply"
						:placeholder="placeholder"
						rounded="lg"
						variant="outlined"
						field-noun="comment"
						:max-length="255"
						@input="onInput"
						@focus="focusComment"
						:hide-details="hideCounter"
						@blur="blurComment"
						auto-grow
						:max-rows="4"
						:rows="1"
						:class="{
							'hide-scrollbars': isDesktop
						}"
						@keydown="editNotesKeyDown"
						@keydown.ctrl.enter="addComment"
						@keydown.meta.enter="addComment"
						@click.native="closeMention">
						<template #append-inner>
							<v-icon
								v-test="'comments-send-button'"
								:disabled="disableNewReply"
								:color="sendButtonColor"
								class="opacity-100"
								@click="addComment()">
								icon-paper-plane-stroke
							</v-icon>
						</template>
					</CharacterLimitedTextArea>
				</div>
			</at-ta>
		</v-card>
		<div v-else class="page d-flex justify-space-between">
			<v-avatar v-test="'comment-avatar'" :color="getColor(editComment.User?.Account?.avatarColor)" class="mr-2">
				<span class="team-avatar-text" :style="{ color: getReadableInitialsColor(editComment.User?.Account?.avatarColor) }">{{
					getAgentNameInitials(setStartedByColum(editComment))
				}}</span>
			</v-avatar>
			<div :class="{ 'mention-dropdown-open': isMentionDropdownOpen }" class="rounded-border">
				<div class="d-flex align-center px-2 pt-2">
					<span class="user-name text-truncate">{{ setStartedByColum(editComment) }}</span>
					<div class="date">{{ getDisplayDate(editComment) }}</div>
				</div>
				<at-ta
					ref="mentionDropdown"
					v-click-outside="closeMention"
					name-key="name"
					:members="localUsers"
					:ats="['@']"
					suffix=" "
					hide-on-blur
					class="mention-dropdown"
					:class="{
						'mention-dropdown-mobile': display.smAndDown.value || lightBoxView,
						'mention-dropdown-open-bottom': openMentionDropdownBottom
					}"
					@at="adjustMentionDropdownDimensions">
					<div class="textarea-wrapper">
						<CharacterLimitedTextArea
							v-model="comment"
							ref="commentField"
							tile
							variant="default"
							density="compact"
							flat
							no-resize
							:placeholder="placeholder"
							field-noun="comment"
							:max-length="255"
							:hide-details="hideCounter"
							:max-rows="4"
							:rows="1"
							@input="onInput"
							auto-grow
							:class="{
								'hide-scrollbars': isDesktop
							}"
							@keydown="editNotesKeyDown"
							@click:append="addComment">
						</CharacterLimitedTextArea>
					</div>
				</at-ta>
				<div class="pa-2 d-flex justify-end">
					<v-btn rounded="pill" class="white-bg" elevation="0" color="grey" variant="text" @click="closeEdit">Cancel</v-btn>
					<v-btn
						rounded="pill"
						:disabled="comment == sanitiseComment(editComment.content)"
						color="primary"
						elevation="0"
						@click="editCommentText"
						>Save</v-btn
					>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
	import EventBus from "@/helpers/eventBus";
	import {
		setStartedByColumn,
		getAgentNameInitials,
		getDisplayDate,
		getColor,
		getReadableInitialsColor,
		calculateTimeout
	} from "../helpers/utilities";
	import DeviceUtility from "../helpers/device";
	import AtTa from "vue-at/dist/vue-at-textarea";
	import clickOutside from "../directives/clickOutside";
	import { useDisplay } from "vuetify";
	import { useToast } from "vue-toastification";
	import { countCodePoints } from "@common-ui/utilities/characterCount";

	export default {
		components: {
			AtTa
		},
		directives: {
			clickOutside
		},
		setup() {
			const display = useDisplay();
			const toast = useToast();
			return { display, toast, getColor, getReadableInitialsColor };
		},
		props: {
			projectId: {
				type: String,
				default: null
			},
			isReply: {
				type: Boolean,
				default: false
			},
			placeholder: {
				type: String,
				default: "Start the conversation..."
			},
			editComment: {
				type: Object,
				default: () => ({})
			},
			lightBoxView: {
				type: Boolean,
				default: false
			},
			projectShareUsers: {
				type: Array,
				default: () => []
			},
			users: {
				type: Array,
				default: () => [],
				required: true
			},
			disableNewReply: {
				type: Boolean,
				default: false
			}
		},
		data() {
			return {
				comment: "",
				localUsers: [],
				filteredUsers: [],
				showMentions: false,
				currentUserSelection: null,
				commentPlaceholders: {},
				highlightedParts: [],
				isLandscape: window.matchMedia("(orientation: landscape)").matches,
				commentFieldFocused: false,
				openMentionDropdownBottom: true,
				isMentionDropdownOpen: false
			};
		},
		computed: {
			isDesktop() {
				return DeviceUtility.isDesktop;
			},
			hideCounter() {
				// Keep the counter out of the way until the user engages with the field,
				// but never hide it while there is text — the over-limit warning must
				// survive a blur (LSP-16404 / LSP-16405 follow-up).
				return !this.commentFieldFocused && this.comment.length === 0;
			},
			sendButtonColor() {
				// Stay primary whenever there is text, regardless of focus. Minimising the
				// keyboard blurs the field; the send affordance must not grey out while a
				// typed-but-unposted comment is sitting there (LSP-16440 follow-up).
				return this.comment.trim().length > 0 ? "primary" : "grey";
			}
		},
		watch: {
			projectShareUsers: {
				handler(newVal) {
					this.setLocalUsers(newVal);
				},
				deep: true
			},
			users: {
				handler(newVal) {
					this.localUsers = newVal.map((user) => ({ name: user.name, id: user.id, value: user.id }));
					this.setLocalUsers(this.projectShareUsers);
				},
				deep: true
			},
			comment: {
				handler(newVal) {
					if (this.editComment?.content && newVal !== this.sanitiseComment(this.editComment.content)) {
						this.$emit("comment-edited", true);
					} else if (this.editComment?.content && newVal === this.sanitiseComment(this.editComment.content)) {
						this.$emit("comment-edited", false);
					} else {
						this.$emit("new-comment-edited", newVal.trim().length > 0);
					}
				},
				deep: true
			}
		},
		async mounted() {
			this._onUserRemovedFromProject = (projectShare, projectId) => {
				if (projectId === this.projectId) {
					const id = projectShare.User?.id;
					if (id) {
						this.localUsers = this.localUsers?.filter((u) => u.id !== id);
					}
				}
			};
			EventBus.$on("UserRemovedFromProject", this._onUserRemovedFromProject);
			this._onProjectShareAccessChanged = (item, projectId, accessType) => {
				if (projectId !== this.projectId) return;
				const user = item.User;
				if (!user) return;
				if (accessType === "viewer") {
					this.localUsers = this.localUsers?.filter((u) => u.id !== user.id);
				} else if (accessType === "editor") {
					const alreadyExists = this.localUsers?.some((u) => u.id === user.id);
					if (!alreadyExists) {
						this.localUsers = [
							...this.localUsers,
							{
								name: user.name ? user.name : `${user.firstname} ${user.lastname}`,
								id: user.id,
								value: user.id
							}
						];
					}
				}
			};
			EventBus.$on("ProjectShareAccessChanged", this._onProjectShareAccessChanged);
			this.localUsers = this.users.map((user) => ({ name: user.name, id: user.id, value: user.id }));
			this.setLocalUsers(this.projectShareUsers);
			if (this.editComment?.content?.length > 0) {
				this.comment = this.editComment.content;
				if (this.editComment.mentionedUsers) {
					this.setLocalUsers(this.editComment.mentionedUsers);
				}
				this.onInput();
			}
			window.addEventListener("resize", this.adjustMentionDropdownWidth);
			window.visualViewport?.addEventListener("resize", this.adjustMentionDropdownPosition);
			window.addEventListener("scroll", this.adjustMentionDropdownPosition, true);
		},
		beforeUnmount() {
			if (this._onUserRemovedFromProject) {
				EventBus.$off("UserRemovedFromProject", this._onUserRemovedFromProject);
			}
			if (this._onProjectShareAccessChanged) {
				EventBus.$off("ProjectShareAccessChanged", this._onProjectShareAccessChanged);
			}
			window.removeEventListener("resize", this.adjustMentionDropdownWidth);
			window.visualViewport?.removeEventListener("resize", this.adjustMentionDropdownPosition);
			window.removeEventListener("scroll", this.adjustMentionDropdownPosition, true);
		},
		methods: {
			closeMention() {
				this.$refs.mentionDropdown?.closePanel?.();
				this.isMentionDropdownOpen = false;
			},
			adjustMentionDropdownDimensions() {
				this.isMentionDropdownOpen = true;
				this.adjustMentionDropdownWidth();
				this.adjustMentionDropdownPosition();
			},
			adjustMentionDropdownWidth() {
				this.$nextTick(() => {
					const fieldRootEl = this.$refs.commentField?.$el;
					const dropdownEl = this.$refs.mentionDropdown?.$el?.querySelector?.(".atwho-view");
					if (!fieldRootEl || !dropdownEl) return;

					// Match the full v-textarea field width (including paddings inside the component)
					const width = fieldRootEl.clientWidth || fieldRootEl.getBoundingClientRect().width;
					dropdownEl.style.setProperty("width", `${width}px`, "important");
					dropdownEl.style.setProperty("max-width", `${width}px`, "important");
					dropdownEl.style.setProperty("min-width", `${width}px`, "important");
				});
			},
			adjustMentionDropdownPosition() {
				this.openMentionDropdownBottom = true;
				this.$nextTick(() => {
					const fieldRootEl = this.$refs.commentField?.$el;
					const dropdownEl = this.$refs.mentionDropdown?.$el?.querySelector?.(".atwho-view");
					if (!fieldRootEl || !dropdownEl) return;

					const rect = fieldRootEl.getBoundingClientRect();
					const hostWidth = fieldRootEl.clientWidth || rect.width;
					const viewportOffsetTop = DeviceUtility.isIOS ? window.visualViewport?.offsetTop || 0 : 0;

					// Get the panel element (the actual visible dropdown)
					const panelEl = this.$refs.mentionDropdown?.$el?.querySelector?.(".atwho-panel");

					// Make the dropdown float and not affect layout (force overrides)
					dropdownEl.style.setProperty("position", "fixed", "important");
					dropdownEl.style.setProperty("bottom", "auto", "important");
					dropdownEl.style.setProperty("right", "auto", "important");
					dropdownEl.style.setProperty("transform", "none", "important");
					dropdownEl.style.setProperty("z-index", "10000", "important");
					dropdownEl.style.setProperty("width", `${hostWidth}px`, "important");

					// Calculate left within viewport bounds
					const clampedLeft = Math.max(0, Math.min(rect.left, window.innerWidth - hostWidth));
					dropdownEl.style.setProperty("left", `${clampedLeft}px`, "important");

					// Always position below the field, ignoring virtual keyboard by anchoring to layout viewport
					const offsetY = 0;
					const top = rect.bottom + viewportOffsetTop + offsetY;
					dropdownEl.style.setProperty("top", `${Math.max(0, top)}px`, "important");
					// Let CSS control max-height (default 250px) to avoid reacting to the keyboard

					// Also style the panel element to ensure it appears above other elements
					if (panelEl) {
						panelEl.style.setProperty("z-index", "10001", "important");
						panelEl.style.setProperty("position", "relative", "important");
					}
				});
			},
			setLocalUsers(newVal) {
				const projectShareUsers = newVal.filter((user) => !!user);
				const projectShareUniqueUsers = projectShareUsers.filter((u) => !this.localUsers.some((user) => user.id === u.id));
				this.localUsers = [
					...this.localUsers,
					...projectShareUniqueUsers.map((user) => ({
						name: user.name ? user.name : `${user.firstname} ${user.lastname}`,
						id: user.id,
						value: user.id
					}))
				];
			},
			ellipsifyUserName(name) {
				if (name.length > 25) {
					return name.substring(0, 22) + "...";
				}
				return name;
			},
			editNotesKeyDown(e) {
				if (["ArrowRight", "ArrowLeft", "ArrowUp", "ArrowDown"].includes(e.code)) {
					e.stopPropagation();
				}
			},
			getEditCommentLength() {
				return this.editComment?.content?.length || 0;
			},
			setStartedByColum(item) {
				return setStartedByColumn(item, this.me?.user.id);
			},
			getAgentNameInitials(agentName) {
				return getAgentNameInitials(agentName);
			},
			getDisplayDate(comment) {
				return getDisplayDate(comment);
			},
			sanitiseComment(comment) {
				if (comment.includes("@")) {
					for (const user of this.localUsers) {
						if (comment.includes(`@${user.id}`)) {
							comment = comment.replaceAll(`@${user.id}`, `@${user.name}`); // we replace the id with the name while rendering the comment.
							this.commentPlaceholders[user.name] = user.id;
						}
					}
				}
				return comment;
			},
			onInput() {
				let comment = this.comment;
				if (comment.includes("@")) {
					for (const user of this.localUsers) {
						if (comment.includes(`@${user.id}`)) {
							comment = comment.replaceAll(`@${user.id}`, `@${user.name}`); // we replace the id with the name while rendering the comment.
							this.commentPlaceholders[user.name] = user.id;
						} else if (comment.includes(`@${user.name}`)) {
							this.commentPlaceholders[user.name] = user.id;
						}
					}
				}
				this.$nextTick(() => {
					if (!this.$refs.commentField) return;
					if (!this.$refs.commentField.style) {
						this.$refs.commentField.style = {};
					}
					this.$refs.commentField.style.height = "auto";
					this.$refs.commentField.style.height = `${this.$refs.commentField?.scrollHeight}px`;
				});
				this.comment = comment;
			},
			addComment() {
				this.updateComment(false);
			},
			editCommentText() {
				// updateComment() early-returns (without saving) for an empty or over-limit comment.
				// Only clear the parent's hasEditedComment flag when the save actually happened —
				// otherwise closing the box skips the unsaved-changes confirm and the in-progress
				// edit is silently discarded (LSP-16852/16859 data-loss regression).
				const wasSaved = this.updateComment(true);
				if (wasSaved) {
					this.$emit("comment-edited", false);
				}
			},
			closeEdit() {
				this.$emit("closeEdit");
				this.$emit("comment-edited", false);
			},
			hideLucky() {
				this.$emit("hide-lucky");
			},
			showLucky() {
				this.$emit("show-lucky");
			},
			clearComment() {
				this.comment = "";
				this.commentPlaceholders = {};
			},
			updateComment(isEditing) {
				const originalComment = this.comment.trim();
				let comment = originalComment;
				const isEmpty = comment.length === 0;
				for (const [name, id] of Object.entries(this.commentPlaceholders)) {
					comment = comment.replaceAll(`@${name}`, `@${id}`); // while saving the comment, we replace the name with the id.
				}
				// Whether substitution actually changed THIS comment — not just whether
				// commentPlaceholders is non-empty, which can hold stale entries for a mention the user
				// already deleted from the text. Only a real substitution may produce
				// mention-specific copy in the toast below.
				const hasSubstitutedMention = comment !== originalComment;
				const displayCodePointCount = countCodePoints(originalComment);
				// Code-point count (LSP-16854) of the value that actually gets PERSISTED — i.e. AFTER
				// @Name -> @<userId> substitution above, not the display text. Comment.content is a
				// varchar(255) column, and a substituted user id is longer than the @Name it replaces, so
				// gating on the pre-substitution display text (LSP-17482 review finding) let a comment
				// that read "250 / 255" on the visible counter balloon past 255 once substituted and fail
				// at the DB layer with no useful error surfaced to the user.
				const codePointCount = countCodePoints(comment);
				const isOverLimit = codePointCount > 255;
				const hasInvisiblePostSubstitutionOverflow = hasSubstitutedMention && displayCodePointCount <= 255 && isOverLimit;
				if (isEmpty || isOverLimit) {
					// Diagnostic (root CLAUDE.md's "Diagnostic logging when fixing silent failures"): this
					// gate's unit just changed (LSP-16854), so name WHICH condition fired and the relevant
					// count — a future "my comment vanished" report must be triageable from logs alone.
					if (isEmpty) {
						console.warn("AddComment.updateComment: blocked — comment is empty after trim, not saving", {
							isEditing,
							projectId: this.projectId,
							commentId: this.editComment?.id ?? null
						});
						// A blank comment is intentionally an inert no-op, regardless of whether Send is clicked
						// or Ctrl/Cmd+Enter invokes this method directly. There is no content to send, so a toast
						// would add noise rather than explain an otherwise surprising refusal.
					}
					if (isOverLimit) {
						console.warn(
							"AddComment.updateComment: blocked — mention-substituted comment exceeds 255 code points, not saving",
							{
								isEditing,
								codePointCount,
								projectId: this.projectId,
								commentId: this.editComment?.id ?? null
							}
						);
						// The inline field rule already explains a directly over-limit display value. Only a real
						// substitution can leave that display value looking valid while the persisted value exceeds
						// the cap, so only that invisible-refusal case needs a toast.
						if (hasInvisiblePostSubstitutionOverflow) {
							const message = "Your comment is too long once @mentions are counted. Please shorten it or remove a mention.";
							this.toast.error(message, { timeout: calculateTimeout(message) });
						}
					}
					return false;
				}
				if (isEditing) {
					this.$emit("update:editComment", comment);
				} else {
					this.$emit("update:addComment", comment);
					this.comment = "";
				}
				return true;
			},
			cancelReply() {
				this.$emit("cancelReply");
			},
			focusComment() {
				this.commentFieldFocused = true;
				if (!this.isDesktop) {
					this.hideLucky();
				}
			},
			blurComment() {
				this.commentFieldFocused = false;
				// Add a small delay to allow click events on the dropdown to register
				// before hiding the dropdown (blur fires before click)
				setTimeout(() => {
					this.isMentionDropdownOpen = false;
					if (!this.isDesktop) {
						this.showLucky();
					}
				}, 200);
			}
		}
	};
</script>

<style lang="css" scoped>
	.hide-scrollbars >>> textarea {
		-ms-overflow-style: none; /* IE and Edge */
		scrollbar-width: none;
	}

	.hide-scrollbars >>> textarea::-webkit-scrollbar {
		display: none;
		-ms-overflow-style: none; /* IE and Edge */
		scrollbar-width: none;
	}

	.date {
		font-size: 0.8rem;
		color: #999;
		margin-left: 10px;
	}

	.v-card.page {
		padding: 0px !important;
		width: 95% !important;
		margin-left: 2.5%;
	}

	.replies-line {
		display: flex;
		align-items: center;
		color: black;
		opacity: 0.6;
	}

	.replies-line .line {
		flex-grow: 1;
		height: 1px;
		background-color: black;
		opacity: 0.6;
		margin-left: 10px;
	}

	.rounded-border {
		border: 1px solid black;
		border-radius: 12px;
		width: 100%;
	}

	.v-input.v-input--hide-details {
		margin-bottom: 0px !important;
	}

	/* custom css for at-ta component to match other fields in the app */

	.mention-dropdown ::v-deep .atwho-view {
		padding: 8px 0px !important;
		background-color: white !important;
		border-radius: 0 !important;
	}

	.mention-dropdown ::v-deep .atwho-ul {
		padding-left: 0px !important;
		max-height: 250px !important;
	}

	.mention-dropdown ::v-deep .atwho-ul li {
		min-height: 48px !important;
		align-items: center;
		display: flex;
		padding: 0px 16px !important;
	}

	.mention-dropdown ::v-deep .atwho-ul li span {
		padding: 12px 0px !important;
	}

	.mention-dropdown ::v-deep .atwho-ul li:hover {
		cursor: pointer;
	}

	.mention-dropdown ::v-deep .atwho-cur {
		background: rgb(var(--v-theme-primary)) !important;
	}

	.mention-dropdown-mobile ::v-deep .atwho-panel {
		left: 16px !important;
	}

	.comment-input-card {
		position: relative;
		z-index: 3001;
		overflow: visible;
	}

	.mention-dropdown-open {
		position: relative;
		z-index: 3001;
		overflow: visible;
	}

	.user-name {
		font-size: 0.8rem;
		color: rgba(var(--v-theme-ink), 0.6);
		max-width: 150px !important;
	}
</style>
